import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewRef,
    ViewChild,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { ModalService } from "../modal/modal.service";
import { Task } from "../../pojo/Task";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { PopoverComponent } from "../s25-popover/popover.component";
import { AvailWeekly } from "../s25-avail-weekly/s25.avail.weekly.util";
import { EventService } from "../../services/event.service";
import { EventData, EventProfile } from "../../services/event.service";
import { FlsService } from "../../services/fls.service";
import { AccessLevels, Fls } from "../../pojo/Fls";
import { OlsService } from "../../services/ols.service";

export interface TaskEditI {
    itemName: string;
    itemTypeId: Task.Id;
    itemId: number;
    eventId: number;
    itemStateId: Task.State;
    assignedToId: number;
    isTodo: boolean;
    todoType: Task.Todo.Type;
    listModelBean: any;
    showClass?: boolean;
    taskBlocked?: boolean;
    workflowChained?: boolean;
    itemQuantity?: string | number;
    objectType?: number;
    objectId?: number;
    competes?: Task.SpaceCompete[];
    reservationId?: number | null;
    eventName?: string;
}

@TypeManagerDecorator("s25-ng-task-edit")
@Component({
    selector: "s25-ng-task-edit",
    template: `
        <span class="ngBold ngInlineBlock {{ hasModal ? 'ngAnchor' : '' }}">
            <span (click)="click()" (keydown.enter)="click()" tabindex="0">
                @if (this.showIcon) {
                    <span>
                        @if (taskModel.itemStateId === 1) {
                            <s25-ng-icon [type]="'taskDefault'" [label]="'taskDefault'"></s25-ng-icon>
                        } @else if (taskModel.itemStateId === 3 || taskModel.itemStateId === 99) {
                            <s25-ng-icon [type]="'taskCancelled'" [label]="'taskCancelled'"></s25-ng-icon>
                        } @else if (taskModel.itemStateId === 2) {
                            <s25-ng-icon [type]="'taskComplete'" [label]="'taskComplete'"></s25-ng-icon>
                        }
                    </span>
                }

                {{ taskModel.itemName }}
                @if (taskModel.objectType === 6 && taskModel.itemQuantity) {
                    <span class="resource-quantity">{{ " [Quantity: " + taskModel.itemQuantity + "]" }}</span>
                }
            </span>
        </span>

        @if (taskModel?.competes?.length > 0) {
            <span class="waitlist-icon ngBold ngInlineBlock">
                <s25-popover
                    [popoverTemplate]="popoverContent"
                    [openTrigger]="'click'"
                    [closeTrigger]="'click'"
                    (mouseenter)="onPopoverTrigger()"
                >
                    <s25-ng-icon-button [type]="'queue'" [label]="'Waitlist Order'"></s25-ng-icon-button
                    >{{ competes?.length }}
                </s25-popover>
                <ng-template #popoverContent>
                    <span ((mouseleave)="onPopoverMouseLeave()" (mouseenter)="onPopoverMouseEnter()">
                        <div class="waitlist-header ngBold">Waitlist Order</div>
                        @for (item of competes; track item.conflictRsrvId; let i = $index) {
                            <div class="content-wrapper">
                                <b>{{ i + 1 | ordinalSuffix }}</b> {{ item?.conflictName }}
                            </div>
                        }
                        @if (goToObjectParams() && hasPerm()) {
                            <div class="go-to-link">
                                <a
                                    s25-ng-router-link
                                    [stateName]="'locationAvailabilityWeekly'"
                                    [stateParams]="goToObjectParams()"
                                    (click)="goToObject()"
                                    >Go To Location</a
                                >
                            </div>
                        }
                    </span>
                </ng-template>
            </span>
        }
    `,
    standalone: false,
    styles: `
        .waitlist-header {
            padding: 8px 15px;
            background-color: var(--color--surface-2-bg);
            position: relative;
            left: 0;
            right: 0;
            margin: -16px -16px 0 -16px;
        }
        a {
            background-color: var(--color--surface-2-bg);
            color: var(--color--link-text);
            padding: 6px 12px;
            border-radius: 3px;

            &:hover {
                background-color: var(--color--surface-2-bg-hover);
                color: var(--color--link-text-hover);
            }
        }

        .waitlist-icon {
            padding: 10px 0px;
            color: #c00 !important;

            ::ng-deep s25-ng-icon-button s25-ng-icon svg {
                color: #c00 !important;
                fill: #c00 !important;
                stroke: #c00 !important;
            }
        }

        .content-wrapper {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .go-to-link {
            text-align: center;
            padding-top: 10px;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
})
export class S25TaskEditComponent implements OnInit {
    @ViewChild(PopoverComponent) popover: PopoverComponent;
    @Input() taskModel: TaskEditI;
    @Input() showIcon?: boolean = false;
    @Input() taskBlocked?: boolean = false;
    @Output() onRefresh = new EventEmitter();
    hasModal: boolean;
    taskIcon: string = "";
    competes: Task.SpaceCompete[];

    private static openPopover: PopoverComponent | null = null;
    private timeoutId: ReturnType<typeof setTimeout> | null = null;

    goToObjectParams = signal<{
        itemTypeId: number;
        itemId: number;
        startDt: string;
        weeks: number;
    } | null>(null);

    hasPerm = signal<boolean>(false);

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    ngOnInit() {
        if (this.taskModel?.competes?.length > 0) {
            // included  current task to dispay the popover waitlist
            this.competes = [
                ...(this.taskModel?.competes ?? []),
                {
                    spaceId: this.taskModel?.objectId,
                    conflictType: "req_rsrv",
                    conflictId: this.taskModel.eventId,
                    conflictRsrvId: this.taskModel.reservationId as number,
                    conflictName: this.taskModel?.eventName,
                },
            ].sort((a, b) => a.conflictRsrvId - b.conflictRsrvId);
        }

        let itemTypeId = S25Util.parseInt(this.taskModel.itemTypeId);
        //only these task types have modals (not v-cal to-dos (type 6)...) OR taslBlocked and workflowChained
        this.hasModal = [1, 2, 3, 4, 5].indexOf(itemTypeId) > -1 && !this.taskBlocked;
        if (this.taskModel.taskBlocked && this.taskModel.workflowChained) {
            this.hasModal = false;
        }

        this.cd && !(this.cd as ViewRef).destroyed && this.cd.detectChanges();
        // this.cd.detectChanges();
    }

    click() {
        if (this.hasModal) {
            ModalService.modal("create-edit-task", {
                taskType: S25Util.parseInt(this.taskModel.itemTypeId),
                taskId: this.taskModel.itemId,
                type: "edit",
                eventId: this.taskModel.eventId,
                taskActionsCell: this.taskModel,
                listModelBean: this.taskModel.listModelBean,
                taskBlocked: this.taskBlocked,
                objectType: this.taskModel?.objectType,
                objectId: this.taskModel?.objectId,
                editCompCallBack: this.editCompCallBack,
            });
        }
    }

    //need to call back and refresh the dataset
    editCompCallBack = () => {
        this.onRefresh.emit();
    };

    refresh() {
        this.cd.detectChanges();
    }

    async calculateGoToObjectParams() {
        const eventData: EventData = await EventService.getEventIncludeCachedLong(
            this.taskModel.eventId,
            ["reservations"],
            true,
            false,
        );

        const profiles: EventProfile[] = S25Util.array.forceArray(eventData?.events?.event?.profile);

        const matchedProfile = profiles.find((profile) =>
            S25Util.array
                .forceArray(profile?.reservation)
                .some((res) => res?.reservation_id === this.taskModel?.reservationId),
        );

        //If no matching profile, use the start/end date of the event
        if (!matchedProfile) {
            const firstStartDate = eventData?.events?.event?.start_date;
            const lastEndDate = eventData?.events?.event?.end_date;
            const getWeeks: number = S25Util.date.diffWeeks(firstStartDate, lastEndDate);
            const getDows = AvailWeekly.getDowsFromDates([new Date(firstStartDate)]);
            return {
                itemTypeId: 4,
                itemId: this.taskModel.objectId,
                startDt: S25Util.date.toS25ISODateStr(new Date(firstStartDate)),
                weeks: getWeeks,
                dows: getDows,
            };
        }

        const reservations = S25Util.array.forceArray(matchedProfile.reservation);
        const sortedReservations = reservations.sort(S25Util.shallowSortDates("event_start_dt", "event_end_dt"));

        const firstStartDate = sortedReservations[0]?.event_start_dt;
        const lastEndDate = sortedReservations[sortedReservations.length - 1]?.event_end_dt;

        const startDates = sortedReservations.map((item) => new Date(item.event_start_dt));
        const getWeeks: number = S25Util.date.diffWeeks(firstStartDate, lastEndDate);
        //TODO: get the dows and pass in as params
        // const getDows = AvailWeekly.getDowsFromDates(startDates);

        return {
            itemTypeId: 4,
            itemId: this.taskModel.objectId,
            startDt: S25Util.date.toS25ISODateStr(new Date(firstStartDate)),
            weeks: getWeeks,
        };
    }

    async goToObject() {
        this.popover.forceClose();
    }

    async onPopoverTrigger() {
        // Close any previously open popover
        if (S25TaskEditComponent.openPopover && S25TaskEditComponent.openPopover !== this.popover) {
            S25TaskEditComponent.openPopover.forceClose();
        }

        const params = await this.calculateGoToObjectParams();

        //calculate the params for the goToObject
        this.goToObjectParams.set(params);

        // perm for go to object
        const [fls, ols] = await Promise.all([
            FlsService.getFls(),
            OlsService.getOls([this.taskModel.objectId], 4, "edit"),
        ]);

        const hasFLSccess = fls?.SPACE_PERM === AccessLevels.Full;
        const hasOlsAccess = Array.isArray(ols) && ols.some((o) => o.access_level !== AccessLevels.None);

        this.hasPerm.set(hasFLSccess || hasOlsAccess);

        // Open this one
        this.popover.forceOpen();
        S25TaskEditComponent.openPopover = this.popover;
    }
    onPopoverMouseLeave() {
        // Delay close
        this.timeoutId = setTimeout(() => {
            this.popover.forceClose();
            if (S25TaskEditComponent.openPopover === this.popover) {
                S25TaskEditComponent.openPopover = null;
            }
        }, 60);
    }

    onPopoverMouseEnter() {
        // Cancel close if user re-enters popover
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }
}
